import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { appointmentOrdersApi } from '../../../features/appointments/api/appointmentOrdersApi';

interface PaymentModeBreakdownProps {
  filteredData: any[];
  backendPaymentModes: Record<string, number>;
}

const PaymentModeBreakdown: React.FC<PaymentModeBreakdownProps> = ({ 
  filteredData, 
  backendPaymentModes 
}) => {
  const [enhancedPaymentModes, setEnhancedPaymentModes] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPaymentData = async () => {
      console.log('PaymentModeBreakdown: Starting fetch', { 
        backendPaymentModesCount: Object.keys(backendPaymentModes).length,
        filteredDataCount: filteredData.length 
      });

      // If backend already has payment mode data, use it
      if (Object.keys(backendPaymentModes).length > 0) {
        console.log('PaymentModeBreakdown: Using backend payment modes', backendPaymentModes);
        setEnhancedPaymentModes(backendPaymentModes);
        setLoading(false);
        return;
      }

      // If no filtered data, set empty and return
      if (!filteredData || filteredData.length === 0) {
        console.log('PaymentModeBreakdown: No filtered data available');
        setEnhancedPaymentModes({});
        setLoading(false);
        return;
      }

      // Otherwise, fetch from appointments using the API
      setLoading(true);
      const paymentModeData: Record<string, number> = {};

      try {
        let totalAppointments = 0;
        let processedAppointments = 0;

        for (const patient of filteredData) {
          if (patient.appointments) {
            totalAppointments += patient.appointments.length;
            for (const appointment of patient.appointments) {
              try {
                console.log(`PaymentModeBreakdown: Fetching payment data for appointment ${appointment.id}`);
                // Fetch actual payment collections summary using the same API as AppointmentDetails
                const summary = await appointmentOrdersApi.getPaymentCollectionsSummary(appointment.id);
                console.log(`PaymentModeBreakdown: Got summary for ${appointment.id}:`, summary);
                
                // Aggregate payment modes
                if (summary.cash > 0) {
                  paymentModeData['CASH'] = (paymentModeData['CASH'] || 0) + summary.cash;
                }
                if (summary.card > 0) {
                  paymentModeData['CARD'] = (paymentModeData['CARD'] || 0) + summary.card;
                }
                if (summary.upi > 0) {
                  paymentModeData['UPI'] = (paymentModeData['UPI'] || 0) + summary.upi;
                }
                if (summary.netbanking > 0) {
                  paymentModeData['NET_BANKING'] = (paymentModeData['NET_BANKING'] || 0) + summary.netbanking;
                }
                if (summary.other > 0) {
                  paymentModeData['OTHER'] = (paymentModeData['OTHER'] || 0) + summary.other;
                }
                if (summary.waveOff > 0) {
                  paymentModeData['WAVE_OFF'] = (paymentModeData['WAVE_OFF'] || 0) + summary.waveOff;
                }
                processedAppointments++;
              } catch (error: any) {
                // Skip appointments without payment data (404 errors are expected)
                if (error?.status !== 404) {
                  console.error('Error fetching payment data for appointment:', appointment.id, error);
                }
                processedAppointments++;
              }
            }
          }
        }

        console.log(`PaymentModeBreakdown: Processed ${processedAppointments}/${totalAppointments} appointments`);
        console.log('PaymentModeBreakdown: Final payment mode data:', paymentModeData);
      } catch (error) {
        console.error('Error fetching payment mode data:', error);
      } finally {
        setLoading(false);
      }

      setEnhancedPaymentModes(paymentModeData);
    };

    fetchPaymentData();
  }, [JSON.stringify(filteredData), JSON.stringify(backendPaymentModes)]);

  if (loading) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary" align="center">
          Loading payment mode data...
        </Typography>
      </Grid>
    );
  }

  if (Object.keys(enhancedPaymentModes).length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary" align="center">
          No payment mode data available for the selected date range.
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      {Object.entries(enhancedPaymentModes).map(([mode, amount]) => (
        <Grid item xs={12} sm={6} md={3} key={mode}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="success.main">
                ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {mode.replace('_', ' ')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  );
};

export default PaymentModeBreakdown;
